// src/components/cards/BaseCard.jsx
import React from 'react';
import { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Logger } from '../../utils/logger';
import { HEADER_TEXT } from '../../utils/colors';
import { alpha } from '@mui/material/styles';

const logger = new Logger('BaseCard');

/**
 * BaseCard provides the foundational card component with drag-and-drop functionality
 * that is used by other card types (Employee, Project, Note, etc.)
 */
const BaseCard = ({
  id,
  title,
  subtitle,
  children,
  onClose,
  onClick,
  position,
  zIndex = 10,
  stackPosition = 5,
  cardType = 'default',
  style = {},
  onPositionChange,
  headerColor,
  onDragStart,
  onDragEnd,
  disableDragging = false,
  onAddToLayout
}) => {
  const theme = useTheme();
  const cardRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cardPosition, setCardPosition] = useState(position || { x: 0, y: 0 });
  const [dragZIndex, setDragZIndex] = useState(null);
  const [pulsing, setPulsing] = useState(false);

  // Handle touch start for mobile/tablet support
  const handleTouchStart = useCallback((e) => {
    if (!cardRef.current) return;

    // Prevent dragging when touching buttons or other interactive elements
    if (
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('.MuiSlider-root') ||
      e.target.closest('input') ||
      e.target.closest('label')
    ) {
      return;
    }

    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const startLeft = cardPosition.x;
    const startTop = cardPosition.y;

    setIsDragging(true);
    setDragZIndex(1000);

    if (onDragStart) {
      onDragStart();
    }

    const handleTouchMove = (moveEvent) => {
      moveEvent.preventDefault();
      const touch = moveEvent.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      const newPosition = {
        x: startLeft + dx,
        y: startTop + dy
      };

      logger.debug(`[TOUCH-DRAG] Card ${id} moving to:`, newPosition);
      setCardPosition(newPosition);
    };

    const handleTouchEnd = (upEvent) => {
      try {
        const touch = upEvent.changedTouches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        const finalPosition = {
          x: startLeft + dx,
          y: startTop + dy
        };

        logger.debug(`[TOUCH-DROP] Card ${id} final position:`, finalPosition);

        setCardPosition(finalPosition);
        setIsDragging(false);
        setDragZIndex(null);

        if (onDragEnd) {
          onDragEnd();
        }

        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);

        if (onPositionChange) {
          logger.debug(`[TOUCH-DROP] Calling onPositionChange with:`, finalPosition);
          onPositionChange({ ...finalPosition });
        }
      } catch (error) {
        logger.error('[TOUCH-DROP] Error in handleTouchEnd:', error);
      }
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [cardPosition, onPositionChange, id, onDragStart, onDragEnd]);

  // Handle dragging within the workspace (not using react-beautiful-dnd)
  // This is for free-form dragging outside of a droppable context
  const handleMouseDown = useCallback((e) => {
    if (!cardRef.current || e.button !== 0) return;
    
    // Prevent dragging when clicking on buttons or other interactive elements
    if (
      e.target.closest('button') || 
      e.target.closest('a') || 
      e.target.closest('.MuiSlider-root') ||
      e.target.closest('input') ||
      e.target.closest('label')
    ) {
      return;
    }
    
    e.preventDefault();
    
    // Get initial positions
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = cardPosition.x;
    const startTop = cardPosition.y;
    
    // Set dragging state and increase z-index to ensure card is on top while dragging
    setIsDragging(true);
    setDragZIndex(1000); // High z-index to ensure it's above all other cards
    
    // Notify parent component that dragging has started
    if (onDragStart) {
      onDragStart();
    }
    
    // Create mouse move handler
    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      
      const newPosition = {
        x: startLeft + dx,
        y: startTop + dy
      };

      logger.debug(`[DRAG] Card ${id} moving to:`, newPosition);
      setCardPosition(newPosition);
      
      // Don't call onPositionChange during drag, only track locally
      // This prevents premature column determination
    };
    
    // Create mouse up handler to clean up
    const handleMouseUp = (upEvent) => {
      try {
        // Calculate the final position directly from the event
        // This ensures we have the most accurate position at the end of the drag
        const dx = upEvent.clientX - startX;
        const dy = upEvent.clientY - startY;
        
        const finalPosition = {
          x: startLeft + dx,
          y: startTop + dy
        };

        logger.debug(`[DROP] Card ${id} final position:`, finalPosition);
        
        // Update the card position state
        setCardPosition(finalPosition);
        
        // Reset dragging state
        setIsDragging(false);
        setDragZIndex(null);
        
        // Notify parent component that dragging has ended
        if (onDragEnd) {
          onDragEnd();
        }
        
        // Remove event listeners
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);


        if (onPositionChange) {
          logger.debug(`[DROP] Calling onPositionChange with:`, finalPosition);
          onPositionChange({ ...finalPosition });
        }
      } catch (error) {
        logger.error('[DROP] Error in handleMouseUp:', error);
      }
    };
    
    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [cardPosition, onPositionChange, id, onDragStart, onDragEnd]);
  
  // Update position from props if it changes
  useEffect(() => {
    if (position && !isDragging) {
      setCardPosition(position);
    }
  }, [position, isDragging]);
  
  // Handle close
  const handleClose = (e) => {
    e.stopPropagation();
    if (onClose) onClose();
  };
  
  // Get drag handle props for non-DnD dragging (mouse and touch)
  const dragHandleProps = disableDragging ? {} : {
    onMouseDown: handleMouseDown,
    onTouchStart: handleTouchStart,
    style: { cursor: 'move', touchAction: 'none' }
  };
  
  // Compute card styles based on stack position
  const getCardStyles = () => {
    if (disableDragging) {
      return {
        position: 'relative',
        width: '100%',
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderRadius: 2,
        border: `1px solid ${headerColor ? alpha(headerColor, 0.45) : 'rgba(255,255,255,0.1)'}`,
        boxShadow: 3,
        overflow: 'hidden',
        ...style
      };
    }
    return {
      position: 'absolute',
      left: cardPosition.x,
      top: cardPosition.y,
      width: {
        xs: '90vw',    // Mobile: 90% of viewport width
        sm: '90vw',    // Small screens: 90% of viewport width
        md: '45vw',    // Tablets: 45% of viewport width
        lg: 500        // Desktop: fixed 500px
      },
      maxWidth: 500,   // Never exceed 500px
      bgcolor: 'background.paper',
      color: 'text.primary',
      borderRadius: 2,
      border: `1px solid ${headerColor ? alpha(headerColor, 0.45) : 'rgba(255,255,255,0.1)'}`,
      boxShadow: isDragging ? 8 : pulsing ? 12 : 3,
      transform: pulsing ? 'scale(1.01)' : 'none',
      transition: isDragging ? 'none' : 'left 0.3s ease-in-out, top 0.3s ease-in-out, box-shadow 0.2s ease, transform 0.15s ease',
      '&:hover': {
        boxShadow: 5,
        border: `1px solid ${headerColor ? alpha(headerColor, 0.7) : 'rgba(255,255,255,0.2)'}`
      },
      zIndex: dragZIndex || zIndex, // Use dragZIndex when dragging, otherwise use the provided zIndex
      overflow: 'hidden',
      ...style
    };
  };
  
  const handleClick = useCallback(() => {
    setPulsing(true);
    setTimeout(() => setPulsing(false), 200);
    if (onClick) onClick();
  }, [onClick]);

  // Non-draggable version (or custom dragging behavior)
  return (
    <Paper
      ref={cardRef}
      sx={getCardStyles()}
      onClick={handleClick}
      elevation={3}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: 1,
          bgcolor: '#1E1E1E',
          color: headerColor || HEADER_TEXT,
          borderRadius: '8px 8px 0 0',
          cursor: 'move',
          userSelect: 'none',
          position: 'relative',
          zIndex: 1
        }}
        {...dragHandleProps}
      >
        {/* Title/Project information on the left */}
        <Box sx={{ overflow: 'hidden', py: 0.5 }}>
          {title}
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: headerColor ? alpha(headerColor, 0.65) : HEADER_TEXT,
                fontSize: '0.65rem',
                mt: 0.25
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Right-side button group: pin + close sit together */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {onAddToLayout && (
            <Tooltip title="Add to workspace" placement="bottom">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onAddToLayout(); }}
                sx={{
                  color: headerColor ? alpha(headerColor, 0.65) : HEADER_TEXT,
                  p: 0.5,
                  mt: 0.25,
                  '&:hover': {
                    bgcolor: headerColor ? alpha(headerColor, 0.12) : 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <PushPinOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onClose && (
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: headerColor ? alpha(headerColor, 0.65) : HEADER_TEXT,
                p: 0.5,
                mt: 0.25,
                '&:hover': {
                  bgcolor: headerColor ? alpha(headerColor, 0.12) : 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      
      {/* Card body content - always fully visible */}
      <Box>
        {children}
      </Box>
    </Paper>
  );
};

BaseCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node,
  onClose: PropTypes.func,
  onClick: PropTypes.func,
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  zIndex: PropTypes.number,
  stackPosition: PropTypes.number,
  cardType: PropTypes.string,
  style: PropTypes.object,
  onPositionChange: PropTypes.func,
  headerColor: PropTypes.string,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  disableDragging: PropTypes.bool,
  onAddToLayout: PropTypes.func
};

export default BaseCard;
